import Phaser from 'phaser';
import type { MorseSymbol } from '@/utils/morse';
import { decodePlayerInput } from '@/utils/morse';
import { THEME } from '@/config/theme';
import { MORSE_SPACING, getInputThresholdMs, type DifficultyConfig } from '@/utils/timing';
import { parseWordBoundaries } from '@/utils/wordBoundaries';

interface LetterGroup {
  symbols: Phaser.GameObjects.Rectangle[];
  symbolInputs: MorseSymbol[];
  letter: Phaser.GameObjects.Text | null;
}

interface ActiveSymbol {
  rect: Phaser.GameObjects.Rectangle;
  startTime: number;
}

export interface SentTrackConfig {
  hitZoneX: number;
  trackY: number;
  letterY: number;
  scrollSpeed: number;
  fadeEndX: number;
  stopX: number;  // X position where letters stop and accumulate
  difficulty: DifficultyConfig;
  mask?: Phaser.Display.Masks.GeometryMask;  // Optional mask for clipping symbols
  onLetterDecoded?: (letter: string) => void;
  intendedMessage?: string;  // Full intended message for word boundary tracking
}

export class SentTrack {
  private scene: Phaser.Scene;
  private config: SentTrackConfig;

  private groups: LetterGroup[] = [];
  private currentInputs: MorseSymbol[] = [];
  private currentSymbols: Phaser.GameObjects.Rectangle[] = [];
  private decodedMessage: string = '';
  private stoppedLetters: Phaser.GameObjects.Text[] = [];
  private lastInputTime: number = 0;
  private lastSymbolWidth: number = MORSE_SPACING.dotWidth;

  // Active symbol being drawn
  private activeSymbol: ActiveSymbol | null = null;
  private readonly MIN_WIDTH = 4;  // Minimum visible width on keydown

  // NEW: Word boundary tracking for formatted messages
  private intendedMessage: string = '';
  private wordLengths: number[] = [];
  private currentWordIndex: number = 0;
  private currentWordPosition: number = 0;
  private formattedMessage: string = '';

  constructor(scene: Phaser.Scene, config: SentTrackConfig) {
    this.scene = scene;
    this.config = config;

    // Initialize word tracking if intended message provided
    if (config.intendedMessage) {
      this.setIntendedMessage(config.intendedMessage);
    }
  }

  private getLetterGapMs(): number {
    // 2 units - same as dot/dash threshold
    const TWO_UNITS = MORSE_SPACING.dotWidth * 2;
    return (TWO_UNITS / this.config.scrollSpeed) * 1000;
  }

  start(): void {
    this.decodedMessage = '';
    this.currentInputs = [];
    this.currentSymbols = [];
    this.groups = [];
    this.stoppedLetters = [];
    this.lastInputTime = 0;
    this.lastSymbolWidth = MORSE_SPACING.dotWidth;
    this.activeSymbol = null;

    // Reset word boundary tracking (but keep intendedMessage and wordLengths)
    this.currentWordIndex = 0;
    this.currentWordPosition = 0;
    this.formattedMessage = '';
  }

  /**
   * Start drawing a new symbol (called on keydown)
   */
  startSymbol(time: number): void {
    if (this.activeSymbol) {
      this.finalizeSymbol(time);
    }

    // Left-anchored rectangle starting at hitZoneX
    const rect = this.scene.add.rectangle(
      this.config.hitZoneX - this.MIN_WIDTH,
      this.config.trackY,
      this.MIN_WIDTH,
      MORSE_SPACING.noteHeight,
      THEME.colors.yellowHex
    );
    rect.setOrigin(0, 0.5);

    if (this.config.mask) {
      rect.setMask(this.config.mask);
    }

    this.activeSymbol = {
      rect,
      startTime: time,
    };
  }

  /**
   * Finalize the active symbol (called on keyup)
   */
  finalizeSymbol(time: number): number {
    if (!this.activeSymbol) return 0;

    const duration = time - this.activeSymbol.startTime;
    const rect = this.activeSymbol.rect;

    // Determine dot or dash based on duration threshold
    const threshold = getInputThresholdMs(this.config.difficulty);
    const symbol: MorseSymbol = duration < threshold ? '.' : '-';

    // Record for decoding
    this.currentInputs.push(symbol);
    this.lastInputTime = time;
    this.lastSymbolWidth = rect.width;

    // Add to scrolling symbols (already left-anchored)
    this.currentSymbols.push(rect);
    this.activeSymbol = null;

    return duration;
  }

  private finalizeCharacter(): void {
    if (this.currentInputs.length === 0) return;

    // Decode the character (existing logic)
    const char = decodePlayerInput(this.currentInputs);
    this.decodedMessage += char;
    this.config.onLetterDecoded?.(char);

    // NEW: Track word boundaries and build formatted message
    if (this.wordLengths.length > 0 && this.currentWordIndex < this.wordLengths.length) {
      // Add space before new word (except for first word)
      if (this.currentWordPosition === 0 && this.formattedMessage.length > 0) {
        this.formattedMessage += ' ';
      }

      // Add the character
      this.formattedMessage += char;
      this.currentWordPosition++;

      // Check if word is complete
      if (this.currentWordPosition >= this.wordLengths[this.currentWordIndex]) {
        this.currentWordIndex++;
        this.currentWordPosition = 0;
      }
    } else {
      // No word tracking or all words complete, just append without spaces
      this.formattedMessage += char;
    }

    // Find center of current symbols
    let sumX = 0;
    for (const s of this.currentSymbols) {
      sumX += s.x + s.width / 2;  // Center of each symbol
    }
    const centerX = sumX / this.currentSymbols.length;

    // Create letter above symbols - use yellow to match sent symbols
    const letter = this.scene.add.text(centerX, this.config.letterY, char, {
      fontFamily: THEME.fonts.mono,
      fontSize: THEME.sizes.heading,
      color: THEME.colors.yellow,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thickness,
    }).setOrigin(0.5);

    // Save group
    this.groups.push({
      symbols: this.currentSymbols,
      symbolInputs: this.currentInputs,
      letter: letter,
    });

    // Reset for next character
    this.currentInputs = [];
    this.currentSymbols = [];
  }

  forceFinalize(): void {
    // Finalize any active symbol first
    if (this.activeSymbol) {
      this.finalizeSymbol(this.scene.time.now);
    }
    this.finalizeCharacter();
  }

  cancelActiveSymbol(): void {
    if (!this.activeSymbol) return;
    this.activeSymbol.rect.destroy();
    this.activeSymbol = null;
  }

  update(time: number, delta: number): void {
    const dx = this.config.scrollSpeed * (delta / 1000);

    // Update active symbol - left edge moves left, right edge stays at hitZoneX
    if (this.activeSymbol) {
      this.activeSymbol.rect.x -= dx;
      this.activeSymbol.rect.width += dx;
    }

    // Check if enough time has passed to finalize current character
    if (this.currentInputs.length > 0 && this.lastInputTime > 0 && !this.activeSymbol) {
      const elapsed = time - this.lastInputTime;
      if (elapsed >= this.getLetterGapMs()) {
        this.finalizeCharacter();
      }
    }

    // Move current symbols (not yet finalized into a character)
    for (const symbol of this.currentSymbols) {
      symbol.x -= dx;
    }

    // Update groups
    for (let i = this.groups.length - 1; i >= 0; i--) {
      const group = this.groups[i];

      // Move symbols
      for (let j = group.symbols.length - 1; j >= 0; j--) {
        const symbol = group.symbols[j];
        symbol.x -= dx;

        // Fade at left boundary
        if (symbol.x < this.config.fadeEndX) {
          symbol.alpha -= 0.1;
          if (symbol.alpha <= 0) {
            symbol.destroy();
            group.symbols.splice(j, 1);
          }
        }
      }

      // Move letter
      if (group.letter) {
        group.letter.x -= dx;

        // Stop at accumulation point
        const nextStopX = this.config.stopX + this.stoppedLetters.length * 20;
        if (group.letter.x <= nextStopX) {
          group.letter.x = nextStopX;
          this.stoppedLetters.push(group.letter);
          group.letter = null;  // Detach from group
        }
      }

      // Remove empty groups
      if (group.symbols.length === 0 && !group.letter) {
        this.groups.splice(i, 1);
      }
    }
  }

  /**
   * Check if currently drawing a symbol
   */
  isDrawing(): boolean {
    return this.activeSymbol !== null;
  }

  isEmpty(): boolean {
    return this.currentSymbols.length === 0 &&
           this.groups.every(g => g.symbols.length === 0) &&
           !this.activeSymbol;
  }

  setScrollSpeed(speed: number): void {
    if (!Number.isFinite(speed) || speed <= 0) return;
    this.config.scrollSpeed = speed;
    this.config.difficulty.scrollSpeed = speed;
  }

  getDecodedMessage(): string {
    return this.decodedMessage;
  }

  /**
   * Gets the formatted decoded message with spaces based on word boundaries
   */
  getFormattedDecodedMessage(): string {
    return this.formattedMessage;
  }

  /**
   * Parses the intended message and sets up word tracking
   * Example: "HELLO WORLD" -> wordLengths = [5, 5]
   */
  setIntendedMessage(message: string): void {
    this.intendedMessage = message;
    // Parse message into word lengths using pure utility
    const result = parseWordBoundaries(message);
    this.wordLengths = result.wordLengths;
    // Reset word tracking
    this.currentWordIndex = 0;
    this.currentWordPosition = 0;
  }

  getStoppedLetterCount(): number {
    return this.stoppedLetters.length;
  }

  removeStoppedLetters(count: number): void {
    if (count <= 0) return;
    const removeCount = Math.min(count, this.stoppedLetters.length);
    const removed = this.stoppedLetters.splice(0, removeCount);
    removed.forEach(letter => letter.destroy());
    for (let i = 0; i < this.stoppedLetters.length; i++) {
      this.stoppedLetters[i].x = this.config.stopX + i * 20;
    }
  }

  destroy(): void {
    if (this.activeSymbol) {
      this.activeSymbol.rect.destroy();
      this.activeSymbol = null;
    }
    for (const s of this.currentSymbols) s.destroy();
    for (const g of this.groups) {
      for (const s of g.symbols) s.destroy();
      g.letter?.destroy();
    }
    for (const l of this.stoppedLetters) l.destroy();
    this.currentSymbols = [];
    this.groups = [];
    this.stoppedLetters = [];
    this.decodedMessage = '';
  }
}
